import { createViewConfig, assignProps, view, useIsomorphicLayoutEffect } from '@sail/engine';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { focusWithoutScrolling, useFocusRing } from '@sail/react-aria';
import { NAVIGATE_EVENT, Unstable_ActionHrefAdapterContext, Unstable_ActionNavigateContext } from '@sail/global-state';
import { usePress } from './hooks/usePress';
import { css } from '../css';
import { useUiConfig } from '../providers/UiConfigProvider';
export const PREFETCH_EVENT = `sail:prefetch`;
const focusRing = css({
    ':focus-visible': {
        focusRing: 'focus'
    }
});
function getHrefType(href) {
    if (!href) {
        return 'empty';
    }
    if (href.startsWith('mailto:')) {
        return 'mailto';
    }
    const hasScheme = !!href.match(/^\w+:\/\//);
    return !hasScheme || typeof window !== 'undefined' && href.indexOf(window.location.origin) === 0 ? 'internal' : 'external';
}
// Unfortunately, `usePress()` doesn't pass along the original event
// for us to cancel when we handle navigation client-side.
// Instead, our strategy is to keep track of the active event target
// if navigation is handled client-side and match it up with the event target
// in our own click handler to signal we should cancel native browser navigation.
let activeEventTarget = null;
let activeKeyboardPress = null;
function shouldUseClientRouter({ download, href, target }) {
    return getHrefType(href) === 'internal' && target !== '_blank' && !download;
}
export function useClientRouting(handlers, options = {}) {
    const onPrefetch = useRef(handlers.onPrefetch);
    const onNavigate = useRef(handlers.onNavigate);
    useEffect(()=>{
        onPrefetch.current = handlers.onPrefetch;
        onNavigate.current = handlers.onNavigate;
    });
    useEffect(()=>{
        var _options_target;
        // user passed a target ref, but it hasn't been initialized yet so return early
        if (options.target && options.target.current === undefined) {
            return;
        }
        var _options_target_current;
        const target = (_options_target_current = (_options_target = options.target) === null || _options_target === void 0 ? void 0 : _options_target.current) !== null && _options_target_current !== void 0 ? _options_target_current : window;
        const prefetchHandler = (e)=>{
            var _e_target;
            const href = (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.href;
            if (href) {
                var _onPrefetch_current;
                (_onPrefetch_current = onPrefetch.current) === null || _onPrefetch_current === void 0 ? void 0 : _onPrefetch_current.call(onPrefetch, href, e);
            }
        };
        const navigateHandler = (e)=>{
            var _e_target;
            const href = (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.href;
            if (href) {
                var _onNavigate_current;
                const didNavigate = (_onNavigate_current = onNavigate.current) === null || _onNavigate_current === void 0 ? void 0 : _onNavigate_current.call(onNavigate, href, e);
                if (didNavigate !== false) {
                    e.preventDefault();
                }
            }
        };
        target.addEventListener(PREFETCH_EVENT, prefetchHandler);
        target.addEventListener(NAVIGATE_EVENT, navigateHandler);
        return ()=>{
            target.removeEventListener(PREFETCH_EVENT, prefetchHandler);
            target.removeEventListener(NAVIGATE_EVENT, navigateHandler);
        };
    }, [
        options.target,
        onPrefetch,
        onNavigate
    ]);
}
function isModifiedEvent(event) {
    return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}
export const ActionConfig = createViewConfig({
    name: 'Action',
    props: {},
    defaults: {
        disabled: false,
        tabIndex: 0
    },
    flattens: true
});
export const ActionInnerConfig = createViewConfig({
    name: 'ActionInner',
    props: {},
    flattens: true
});
export const ActionInnerExperimentalConfig = createViewConfig({
    name: 'ActionInnerExperimental',
    props: {},
    flattens: true
});
const ActionInnerExperimental = ActionInnerExperimentalConfig.createView(({ disabled, onClick: onClickProp, onPress: onPressProp, onPressStart: onPressStartProp, onPressEnd, onPressChange, onPressUp, autoFocus, ...props })=>{
    let onPress = onPressProp;
    let onPressStart = onPressStartProp;
    let onClick = onClickProp;
    const useHrefAdapter = React.useContext(Unstable_ActionHrefAdapterContext);
    const useNavigate = React.useContext(Unstable_ActionNavigateContext);
    const hrefAdapter = useHrefAdapter();
    const navigate = useNavigate();
    props.href = hrefAdapter(props.href);
    // https://www.scottohara.me/note/2019/07/17/placeholder-link.html
    if (disabled) {
        delete props.href;
    }
    // To avoid doing a casting a TS control flow analysis won't be modifying optional props keys
    const href = props.href;
    // Configure HTML attributes for a link with an href
    if (href) {
        // Prevent usage of unsafe target='_blank' by setting rel="noopener noreferrer".
        // This could be more nuanced (e.g. only external links) in the future.
        // https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md
        if (props.target === '_blank' && props.rel === undefined) {
            props.rel = 'noopener noreferrer';
        }
        // If this href supports client-side navigation, we add custom events.
        if (shouldUseClientRouter(props)) {
            const performNavigation = (e)=>{
                const cancelable = !isModifiedEvent(e) ? navigate(href, e) : undefined;
                if (cancelable === false) {
                    activeEventTarget = e.target;
                }
            };
            onPressStart = (e)=>{
                const prefetchEvent = new CustomEvent(PREFETCH_EVENT, {
                    bubbles: true,
                    cancelable: true
                });
                e.target.dispatchEvent(prefetchEvent);
                onPressStartProp && onPressStartProp(e);
                activeKeyboardPress = e.pointerType === 'keyboard';
                activeKeyboardPress && performNavigation(e);
            };
            onPress = (e)=>{
                !activeKeyboardPress && performNavigation(e);
                onPressProp && onPressProp(e);
            };
            onClick = (e)=>{
                if (e.button === 0 && activeEventTarget === e.currentTarget) {
                    e.preventDefault();
                }
                activeEventTarget = null;
                onClickProp && onClickProp(e);
            };
        }
    } else {
        // When an href isn’t provided, we set `role="button"` to comply with
        // ARIA best practices.
        //
        // For more information, see:
        // - https://www.w3.org/TR/2016/WD-wai-aria-practices-1.1-20160317/examples/button/button.html
        // - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role
        props.role = props.role || 'button';
        delete props.href;
        // If this is a button, we need to add keyboard events:
        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role#Keyboard_interactions
        // https://www.w3.org/TR/2016/WD-wai-aria-practices-1.1-20160317/examples/button/button.html
        props.onKeyPress = function buttonKeyboardBehavior(e) {
            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                e.preventDefault();
            }
        };
        // If this is a button, we also need to ensure focus is placed onto the button when clicked (unlike with an anchor)
        // https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-3
        onPressStart = (e)=>{
            onPressStartProp && onPressStartProp(e);
        };
    }
    const ref = useRef(null);
    // React handles autoFocus for some elemenst like buttons and inputs, but not for anchors, so we do it manually.
    useIsomorphicLayoutEffect(()=>{
        var _ref_current;
        // eslint-disable-next-line @sail/ui-no-reflow
        autoFocus && ((_ref_current = ref.current) === null || _ref_current === void 0 ? void 0 : _ref_current.focus());
    }, []);
    const { pressProps: { children, ...pressProps } } = usePress({
        isDisabled: disabled,
        onPress,
        onPressStart,
        onPressEnd,
        onPressUp,
        onPressChange,
        preventFocusOnPress: true,
        disablePreventDefault: true,
        ref
    });
    const handleOnClick = (e)=>{
        if (disabled) {
            e.stopPropagation();
            return;
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    return /*#__PURE__*/ React.createElement(view.a, {
        // @ts-expect-error 'tabIndex' is specified more than once, so this usage will be overwritten
        tabIndex: 0,
        ...props,
        ...disabled ? {
            'aria-disabled': true,
            tabIndex: -1
        } : {},
        ref: ref,
        uses: [
            assignProps(pressProps),
            focusRing,
            assignProps({
                onClick: handleOnClick
            })
        ]
    });
}, {
    css: {
        cursor: 'pointer',
        textDecoration: 'none',
        fontSize: 'inherit',
        fontWeight: 'inherit'
    },
    forward: {
        disabled: true
    },
    variants: {
        disabled: {
            true: [],
            false: []
        }
    }
});
const ActionInner = ActionInnerConfig.createView(({ disabled, onClick: onClickProp, onPress: onPressProp, onPressStart: onPressStartProp, onPressEnd, onPressChange, onPressUp, autoFocus, ...props })=>{
    let onPress = onPressProp;
    let onPressStart = onPressStartProp;
    let onClick = onClickProp;
    const useHrefAdapter = React.useContext(Unstable_ActionHrefAdapterContext);
    const useNavigate = React.useContext(Unstable_ActionNavigateContext);
    const hrefAdapter = useHrefAdapter();
    const navigate = useNavigate();
    props.href = hrefAdapter(props.href);
    // It's important to check for the presence of an href before it's deleted in the next code block
    const isButton = !props.href;
    // https://www.scottohara.me/note/2019/07/17/placeholder-link.html
    if (disabled) {
        delete props.href;
    }
    // To avoid doing a casting a TS control flow analysis won't be modifying optional props keys
    const href = props.href;
    if (isButton) {
        // When an href isn’t provided, we set `role="button"` to comply with
        // ARIA best practices.
        //
        // For more information, see:
        // - https://www.w3.org/TR/2016/WD-wai-aria-practices-1.1-20160317/examples/button/button.html
        // - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role
        props.role = props.role || 'button';
        delete props.href;
        // If this is a button, we need to add keyboard events:
        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role#Keyboard_interactions
        // https://www.w3.org/TR/2016/WD-wai-aria-practices-1.1-20160317/examples/button/button.html
        props.onKeyPress = function buttonKeyboardBehavior(e) {
            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                e.preventDefault();
            }
        };
        // If this is a button, we also need to ensure focus is placed onto the button when clicked (unlike with an anchor)
        // https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-3
        onPressStart = (e)=>{
            focusWithoutScrolling(e.target);
            onPressStartProp && onPressStartProp(e);
        };
    } else if (href && !disabled) {
        // Prevent usage of unsafe target='_blank' by setting rel="noopener noreferrer".
        // This could be more nuanced (e.g. only external links) in the future.
        // https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md
        if (props.target === '_blank' && props.rel === undefined) {
            props.rel = 'noopener noreferrer';
        }
        // If this href supports client-side navigation, we add custom events.
        if (shouldUseClientRouter(props)) {
            const performNavigation = (e)=>{
                const cancelable = !isModifiedEvent(e) ? navigate(href, e) : undefined;
                if (cancelable === false) {
                    activeEventTarget = e.target;
                }
            };
            onPressStart = (e)=>{
                const prefetchEvent = new CustomEvent(PREFETCH_EVENT, {
                    bubbles: true,
                    cancelable: true
                });
                e.target.dispatchEvent(prefetchEvent);
                onPressStartProp && onPressStartProp(e);
                activeKeyboardPress = e.pointerType === 'keyboard';
                activeKeyboardPress && performNavigation(e);
            };
            onPress = (e)=>{
                !activeKeyboardPress && performNavigation(e);
                onPressProp && onPressProp(e);
            };
            onClick = (e)=>{
                if (e.button === 0 && activeEventTarget === e.currentTarget) {
                    e.preventDefault();
                }
                activeEventTarget = null;
                onClickProp && onClickProp(e);
            };
        }
    }
    const ref = useRef(null);
    // React handles autoFocus for some elemenst like buttons and inputs, but not for anchors, so we do it manually.
    useIsomorphicLayoutEffect(()=>{
        var _ref_current;
        // eslint-disable-next-line @sail/ui-no-reflow
        autoFocus && ((_ref_current = ref.current) === null || _ref_current === void 0 ? void 0 : _ref_current.focus());
    }, []);
    const { pressProps: { children, ...pressProps } } = usePress({
        isDisabled: disabled,
        onPress,
        onPressStart,
        onPressEnd,
        onPressUp,
        onPressChange,
        preventFocusOnPress: true,
        ref
    });
    const { isFocusVisible, focusProps } = useFocusRing();
    const handleOnClick = (e)=>{
        if (disabled) {
            e.stopPropagation();
            return;
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    return /*#__PURE__*/ React.createElement(view.a, {
        ...props,
        ...disabled ? {
            'aria-disabled': true,
            tabIndex: -1
        } : {},
        ref: ref,
        uses: [
            assignProps(pressProps),
            assignProps(focusProps),
            assignProps({
                onClick: handleOnClick
            }),
            isFocusVisible ? css({
                focusRing: 'focus'
            }) : css({
                focusRing: 'none'
            })
        ]
    });
}, {
    css: {
        cursor: 'pointer',
        textDecoration: 'none',
        fontSize: 'inherit',
        fontWeight: 'inherit'
    },
    forward: {
        disabled: true
    },
    variants: {
        disabled: {
            true: [],
            false: []
        }
    }
});
// We always use an `a` tag for actions instead of a `button` or `input` tag
// because some browsers cannot render `button` or `input` inline.
export const Action = ActionConfig.createView((props)=>{
    const uiConfig = useUiConfig();
    return (uiConfig === null || uiConfig === void 0 ? void 0 : uiConfig.disableReactAriaFocusRing) ? /*#__PURE__*/ React.createElement(ActionInnerExperimental, props) : /*#__PURE__*/ React.createElement(ActionInner, props);
});

//# sourceMappingURL=Action.js.map